import appConfig from "constants/appConfig";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import { modalActions } from "modules/modal";
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { pushDataLayerWithClientBuildUnixTime } from "utility/ga";

import type { Store } from "../store";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GoogleOneTapAPI = { accounts: { id: any } } | undefined;

declare let google: GoogleOneTapAPI;

export const useGoogleOneTap = () => {
  const auth = useSelector((state: Store) => state.auth);
  const dispatch = useDispatch();
  const [googleAPI, setGoogleAPI] = useState<GoogleOneTapAPI>(undefined);

  // google one tapのscriptが読み込まれたら、googleAPIにセット
  useEffect(() => {
    let intervalId: number | undefined = undefined;

    if (typeof google !== "undefined" && google?.accounts?.id) {
      setGoogleAPI(google);
    } else {
      // まだ読み込まれていない場合のみ定期的にチェック
      intervalId = window.setInterval(() => {
        if (typeof google !== "undefined" && google?.accounts?.id) {
          setGoogleAPI(google);
          clearInterval(intervalId); // 見つかったらインターバルを自分でクリア
        }
      }, 100);
    }

    // コンポーネントのクリーンアップ時にインターバルをクリア
    return () => {
      if (intervalId !== undefined) {
        clearInterval(intervalId);
      }
    };
  }, []);

  useEffect(() => {
    try {
      // subdomainの承認済みの JavaScript 生成元のuriをsubdomainごとに設定する必要があるため、とりあえずは
      // urlがhttps://spwn.jp or https://devspwn.netで始まる場合のみ、google one tapを表示する

      // Q.何を動作させるために設定が必要？なぜサブドメインだけ設定が必要？
      // A.subdomainを追加する時には、該当のclientの承認済みの JavaScript 生成元にsubdomainを追加する必要がある (参考:https://zenn.dev/milky/articles/firebase-auth-custom-domain-vercel)
      // Q.すでにGoogle認証はあるのに、なぜ今回one tapで設定が必要になった？
      // A.google one tapでは, firebase authenticationで設定しているredirect_urlを使用しないため、google consoleから直接追加する必要がある

      if (
        !(
          window.location.href.startsWith("https://spwn.jp") ||
          window.location.href.startsWith("https://devspwn.net")
        )
      ) {
        return;
      }

      if (googleAPI === undefined) {
        return;
      }

      if (auth.isLogin === false) {
        const handleCredentialResponse = (response: { credential: string }) => {
          (async () => {
            await firebase
              .auth()
              .signInWithCredential(
                firebase.auth.GoogleAuthProvider.credential(response.credential)
              );
            pushDataLayerWithClientBuildUnixTime({
              event: "sign_up",
              method: "google_one_tap",
            });
          })();
        };

        const client_id = appConfig.firebaseGoogleAuthClientId;
        googleAPI.accounts.id.initialize({
          client_id,
          callback: handleCredentialResponse,
          context: "use",
          //https://developers.google.com/identity/gsi/web/reference/js-reference?hl=ja#state_cookie_domain
          state_cookie_domain: "spwn.jp",
        });
        googleAPI.accounts.id.prompt();
      } else {
        googleAPI.accounts.id.cancel();
      }
    } catch (error) {
      if (error instanceof Error) {
        dispatch(
          modalActions.toggleError({
            msg: `エラーが発生しました。\n ${error.message}`,
          })
        );
      }
      console.error("Google One Tap Error", error);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isLogin, googleAPI]);
};
